import React, { useMemo, useState } from "react";
import {
  DemoResponsiveGrid,
  DemoSlider,
  DemoWorkbenchCard,
  DemoWorkbenchCopy,
  DemoWorkbenchFact,
  DemoWorkbenchFactGrid,
  DemoWorkbenchInset,
  DemoWorkbenchStack,
  type DemoWorkbenchFactTone,
} from "@csk/demo-ui";
import { evaluateDifferentialStructure } from "./differential-structure-panel/differentialStructureModel.js";

function ratioTone(value: number): DemoWorkbenchFactTone {
  if (value > 1.5) return "danger";
  if (value < 0.67) return "accent";
  return "success";
}

function amplificationTone(value: number): DemoWorkbenchFactTone {
  return value > 1.5 ? "danger" : "success";
}

const DifferentialStructurePanel = (): React.JSX.Element => {
  const [authMag, setAuthMag] = useState(3.0);
  const [dataMag, setDataMag] = useState(1.0);
  const [t, setT] = useState(1.0);

  const state = useMemo(
    () => evaluateDifferentialStructure(authMag, dataMag, t),
    [authMag, dataMag, t],
  );

  return (
    <DemoWorkbenchStack>
      <DemoWorkbenchCard title="Differential Structure of GL(2,ℂ) (§13.6)">
        <DemoWorkbenchCopy>
          How the Lie group structure of GL(2,ℂ) manifests as governance drift
          mechanics.
        </DemoWorkbenchCopy>
      </DemoWorkbenchCard>

      <DemoResponsiveGrid minColumnWidth={180} gap={16}>
        <DemoSlider
          id="gl2-auth-magnitude"
          label="|a| (authority)"
          min={0.5}
          max={5}
          step={0.1}
          value={authMag}
          onChange={setAuthMag}
          format={(value) => value.toFixed(1)}
        />
        <DemoSlider
          id="gl2-data-magnitude"
          label="|d| (data)"
          min={0.5}
          max={5}
          step={0.1}
          value={dataMag}
          onChange={setDataMag}
          format={(value) => value.toFixed(1)}
        />
        <DemoSlider
          id="gl2-flow-time"
          label="t (time)"
          min={0.1}
          max={3}
          step={0.1}
          value={t}
          onChange={setT}
          format={(value) => value.toFixed(1)}
        />
      </DemoResponsiveGrid>

      <DemoWorkbenchInset title="exp: Drift → Transformation">
        <DemoWorkbenchFactGrid minColumnWidth={110}>
          {[
            {
              name: "X₁ auth scale",
              val: `e^${t.toFixed(1)} = ${state.expAuthScale.toFixed(2)}`,
              gen: "G1",
            },
            {
              name: "X₂ auth phase",
              val: `e^{i·${t.toFixed(1)}} = ${state.expAuthPhase.re.toFixed(2)}+${state.expAuthPhase.im.toFixed(2)}i`,
              gen: "G3",
            },
            {
              name: "X₃ data scale",
              val: `e^${t.toFixed(1)} = ${state.expDataScale.toFixed(2)}`,
              gen: "G4",
            },
            { name: "X₄ data phase", val: `e^{i·${t.toFixed(1)}}`, gen: "G2" },
          ].map((fact) => (
            <DemoWorkbenchFact
              key={fact.name}
              label={fact.name}
              value={fact.val}
              detail={fact.gen}
              mono
            />
          ))}
        </DemoWorkbenchFactGrid>
      </DemoWorkbenchInset>

      <DemoWorkbenchInset title="Ad: Bridge Amplification">
        <DemoWorkbenchFactGrid minColumnWidth={130}>
          {[
            {
              label: "|a|/|d|",
              value: state.ratio.toFixed(2),
              detail: "governance scale ratio",
              tone: ratioTone(state.ratio),
            },
            {
              label: "auth→data bridge",
              value: `×${state.bridgeAmpAuthToData.toFixed(2)}`,
              detail:
                state.bridgeAmpAuthToData > 1.5
                  ? "AMPLIFIED — high risk"
                  : state.bridgeAmpAuthToData > 1
                    ? "slightly amplified"
                    : "damped",
              tone: amplificationTone(state.bridgeAmpAuthToData),
            },
            {
              label: "data→auth bridge",
              value: `×${state.bridgeAmpDataToAuth.toFixed(2)}`,
              detail:
                state.bridgeAmpDataToAuth > 1.5
                  ? "AMPLIFIED — high risk"
                  : state.bridgeAmpDataToAuth > 1
                    ? "slightly amplified"
                    : "damped",
              tone: amplificationTone(state.bridgeAmpDataToAuth),
            },
          ].map((fact) => (
            <DemoWorkbenchFact
              key={fact.label}
              label={fact.label}
              value={fact.value}
              detail={fact.detail}
              tone={fact.tone}
              mono
            />
          ))}
        </DemoWorkbenchFactGrid>
      </DemoWorkbenchInset>

      <DemoWorkbenchInset title="θ = g⁻¹dg: Proportional Drift Rate">
        <DemoWorkbenchFactGrid minColumnWidth={160}>
          <DemoWorkbenchFact
            label="Authority drift rate"
            value={`a'/a = 1/${authMag.toFixed(1)} = ${state.maurerCartanAuth.toFixed(3)}`}
            detail={`${(state.maurerCartanAuth * 100).toFixed(1)}% per unit time`}
            mono
          />
          <DemoWorkbenchFact
            label="Data drift rate"
            value={`d'/d = 1/${dataMag.toFixed(1)} = ${state.maurerCartanData.toFixed(3)}`}
            detail={`${(state.maurerCartanData * 100).toFixed(1)}% per unit time`}
            mono
          />
        </DemoWorkbenchFactGrid>
        <DemoWorkbenchCopy size="xs" tone="muted" className="mt-2">
          Left-invariant: same drift rate regardless of absolute governance
          level.
        </DemoWorkbenchCopy>
      </DemoWorkbenchInset>

      <DemoWorkbenchInset title="B(X,Y): Killing Form Coupling">
        <DemoWorkbenchCopy tone="muted">B = 2(z₁−z₂)(w₁−w₂)</DemoWorkbenchCopy>
        <DemoWorkbenchCopy className="mt-1">
          Rank 1 on torus: only the difference direction couples.
        </DemoWorkbenchCopy>
        <DemoWorkbenchCopy size="xs" tone="muted" className="mt-1">
          Kernel = {"{"}z₁ = z₂{"}"} = simultaneous auth-data changes =
          SEPARATE at tangent level.
        </DemoWorkbenchCopy>
      </DemoWorkbenchInset>
    </DemoWorkbenchStack>
  );
};

export default DifferentialStructurePanel;
